'use client';
import type { ReactElement, FC, ReactNode } from 'react';
import { createContext, useCallback, useContext } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useDecisionTreeMemory } from '@provider-directory/hooks/useDecisionTreeMemory';
import { SpecialtyBranch } from '@provider-directory/models/decision-tree.model';

export const SHARED_DECISION_TREE_STORAGE_KEY = 'shared-decision-tree';
const DEFAULT_INITIAL_SCENE = 'is-adult';

export type SharedDecisionTreeAnswers = Record<string, unknown>;

export interface SharedDecisionTreeData {
  specialtyBranch: SpecialtyBranch | null;
  answers: SharedDecisionTreeAnswers;
  reasonForVisitId: string | null;
  reasonForVisitKey: string | null;
  lastViewedScene: string | null;
  navigationHistory: string[];
  providerId: string | null;
  overrideProviderId: string | null;
  timestamp: number;
}

export const DEFAULT_SHARED_DECISION_TREE: SharedDecisionTreeData = {
  specialtyBranch: null,
  answers: {},
  reasonForVisitId: null,
  reasonForVisitKey: null,
  lastViewedScene: null,
  navigationHistory: [],
  providerId: null,
  overrideProviderId: null,
  timestamp: Date.now(),
};

export interface SharedDecisionTreeContextType {
  sharedData: SharedDecisionTreeData;
  updateSharedData: (data: Partial<SharedDecisionTreeData>) => void;
  updateAnswer: (sceneSlug: string, answer: unknown) => void;
  updateLastViewedScene: (scene: string) => void;
  updateNavigationHistory: (history: string[]) => void;
  clearSharedData: () => void;
  hasAnswersForSpecialty: (specialty: SpecialtyBranch) => boolean;
  getPrefilledAnswer: (sceneSlug: string) => unknown;
  getLastViewedScene: () => string | null;
  getNavigationHistory: () => string[];
  canNavigateBack: () => boolean;
  getInitialScene: (
    specialtyBranch: SpecialtyBranch | null,
    decisionTreeAnswersUsage?: 'skip' | 'prefill',
  ) => string;
}

const SharedDecisionTreeContext =
  createContext<SharedDecisionTreeContextType | null>(null);

export const SharedDecisionTreeProvider: FC<{ children: ReactNode }> = ({
  children,
}): ReactElement => {
  const [sharedData, setSharedData] = useSessionStorage<SharedDecisionTreeData>(
    SHARED_DECISION_TREE_STORAGE_KEY,
    DEFAULT_SHARED_DECISION_TREE,
  );

  // Memory helpers (pure logic) — ensures single source of truth for skip/prefill rules
  const { isExcluded, computeHydration, cleanHistoryTail } =
    useDecisionTreeMemory();

  const updateSharedData = useCallback(
    (data: Partial<SharedDecisionTreeData>) => {
      setSharedData((prev) => ({
        ...prev,
        ...data,
        timestamp: Date.now(),
      }));
    },
    [setSharedData],
  );

  const updateAnswer = useCallback(
    (sceneSlug: string, answer: unknown) => {
      setSharedData((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [sceneSlug]: answer,
        },
        timestamp: Date.now(),
      }));
    },
    [setSharedData],
  );

  const clearSharedData = useCallback(() => {
    setSharedData(DEFAULT_SHARED_DECISION_TREE);
  }, [setSharedData]);

  const hasAnswersForSpecialty = useCallback(
    (specialty: SpecialtyBranch): boolean => {
      return (
        sharedData.specialtyBranch === specialty &&
        Object.keys(sharedData.answers).length > 0
      );
    },
    [sharedData],
  );

  const getPrefilledAnswer = useCallback(
    (sceneSlug: string): unknown => {
      return sharedData.answers[sceneSlug];
    },
    [sharedData.answers],
  );

  // Use the shared isExcluded to avoid duplicating the logic here.
  const updateLastViewedScene = useCallback(
    (scene: string) => {
      if (isExcluded(scene)) return;

      setSharedData((prev) => ({
        ...prev,
        lastViewedScene: scene,
        timestamp: Date.now(),
      }));
    },
    [setSharedData, isExcluded],
  );

  const getLastViewedScene = useCallback((): string | null => {
    return sharedData.lastViewedScene;
  }, [sharedData.lastViewedScene]);

  const updateNavigationHistory = useCallback(
    (history: string[]) => {
      const sanitizedHistory = cleanHistoryTail(history);

      setSharedData((prev) => ({
        ...prev,
        navigationHistory: sanitizedHistory,
        timestamp: Date.now(),
      }));
    },
    [setSharedData, cleanHistoryTail],
  );

  const getNavigationHistory = useCallback((): string[] => {
    return sharedData.navigationHistory;
  }, [sharedData.navigationHistory]);

  const canNavigateBack = useCallback((): boolean => {
    return sharedData.navigationHistory.length > 0;
  }, [sharedData.navigationHistory]);

  // Delegate initial scene decision to computeHydration to keep rules consistent with the modal.
  const getInitialScene = useCallback(
    (
      specialtyBranch: SpecialtyBranch | null,
      decisionTreeAnswersUsage?: 'skip' | 'prefill',
    ): string => {
      if (!specialtyBranch) {
        return DEFAULT_INITIAL_SCENE;
      }

      const plan = computeHydration({
        shared: {
          specialtyBranch: sharedData.specialtyBranch,
          answers: sharedData.answers,
          navigationHistory: sharedData.navigationHistory,
          lastViewedScene: sharedData.lastViewedScene,
          providerId: sharedData.providerId,
        },
        specialtyBranch,
        answersUsage: decisionTreeAnswersUsage,
      });

      return plan.resumeScene ?? DEFAULT_INITIAL_SCENE;
    },
    [sharedData, computeHydration],
  );

  return (
    <SharedDecisionTreeContext.Provider
      value={{
        sharedData,
        updateSharedData,
        updateAnswer,
        updateLastViewedScene,
        updateNavigationHistory,
        clearSharedData,
        hasAnswersForSpecialty,
        getPrefilledAnswer,
        getLastViewedScene,
        getNavigationHistory,
        canNavigateBack,
        getInitialScene,
      }}
    >
      {children}
    </SharedDecisionTreeContext.Provider>
  );
};

export const useSharedDecisionTree = (): SharedDecisionTreeContextType => {
  const context = useContext(SharedDecisionTreeContext);

  if (!context) {
    throw new Error(
      'useSharedDecisionTree must be used within SharedDecisionTreeProvider',
    );
  }
  return context;
};
